// Pagination.tsx
import React from 'react';
import { LeftPaginationIcon } from '../../../../common/components/icons/left-pagination.component';
import { RightPaginationIcon } from '../../../../common/components/icons/right-pagination.component';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  if (totalPages <= 1) return null;

  const pages: (number | string)[] = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  const renderedPages = pages.reduce<React.ReactNode[]>((acc, page, index) => {
    if (page === '...') {
      acc.push(
        <span
          key={`dots-${index}`}
          aria-hidden="true"
          className="text-slate-400 w-[32px] h-[32px] flex items-center justify-center"
        >
          ...
        </span>
      );
    } else if (typeof page === 'number') {
      acc.push(
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`w-[32px] h-[32px] flex items-center justify-center rounded-full text-sm font-medium transition-all ${
            currentPage === page
              ? 'bg-content-primary-alt text-surface-base'
              : 'border border-border-alt bg-surface-secondary-alt text-content-primary-alt'
          }`}
        >
          {page}
        </button>
      );
    }
    return acc;
  }, []);

  return (
    <div className="flex justify-center items-center gap-[8px] mt-[32px]">
      <button
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
        aria-label="Previous page"
        className="w-[32px] h-[32px] flex items-center justify-center"
      >
        <LeftPaginationIcon />
      </button>

      {renderedPages}

      <button
        onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        aria-label="Next page"
        className="w-[32px] h-[32px] flex items-center justify-center"
      >
        <RightPaginationIcon />
      </button>
    </div>
  );
};

export default Pagination;
